import { Col, Typography } from "antd";
import AppAnimate from "@lib/components/AppComponents/AppAnimate";
import AppRowContainer from "@lib/components/AppComponents/AppRowContainer";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import {
  DateRangeFilter,
  SelectedPaymentProviderSelectBox,
} from "@lib/components/MiniComponents/Others";
import MiniInfoCard from "@lib/components/MiniComponents/Dashboard/MiniInfoCard";
import { FaFilter } from "react-icons/fa";
import { blue } from "@ant-design/colors";
import { useIntl } from "react-intl";
import {
  PaymentHistoriesMiniCard,
  PaymentProvidersMiniCard,
} from "@app/admin/dashboards/components/MiniCards";
import {
  ApiParams,
  AppContext,
  CurrencyEnum,
  TransactionProxyStatus,
} from "@lib/types";
import CustomPieChart from "@lib/components/MiniComponents/Dashboard/PieChart";
import Revenue from "@lib/components/MiniComponents/Dashboard/Revenue";
import { useMemo } from "react";
import { transactionStatusColor } from "@lib/helpers/ColorHelper";
import { Dayjs } from "dayjs";
import { apiGetTransactions } from "@lib/api/transaction";
import { useQuery } from "@tanstack/react-query";
import { AwaitingApprovalPayments } from "@lib/components/MiniComponents/Dashboard/Payment";
import { useDateRange } from "@lib/hooks/useDateRange";
import { SharedPaymentHistory } from "@app/shared";
import { StyledFlexDiv } from "@lib/components/StyledComponents";
import { Link } from "react-router-dom";
import { ROUTES } from "@lib/constants";

export default function PaymentProviderDashboard() {
  const { messages } = useIntl();

  const { dateBetween, setDateBetween } = useDateRange({
    initialRange: "monthly",
    cache: true,
  });

  const params = useMemo(() => {
    return {
      s: [
        {
          $and: [
            { createdAt: { $gte: dateBetween[0] } },
            { createdAt: { $lte: dateBetween[1] } },
          ],
        },
      ],
      join: ["customer", "provider"],
    } as ApiParams;
  }, [dateBetween]);

  const handleDateBetween = (date: [Dayjs, Dayjs]) => {
    setDateBetween(date);
  };

  const paymentHistoryQuery = useQuery({
    queryKey: [
      "payment-provider-dashboard-transactions",
      JSON.stringify(params),
    ],
    queryFn: () => apiGetTransactions({ ...params }),
  });
  const apiData = paymentHistoryQuery?.data?.data?.data;
  const currency = CurrencyEnum.USD;
  const paymentHistoryTotalAmount = useMemo(() => {
    let amount = 0;
    if (apiData) {
      for (let index = 0; index < apiData.length; index++) {
        amount += Number(
          apiData[index].convertedAmount || apiData[index].amount,
        );
      }
    }
    return amount;
  }, [apiData]);

  const paymentHistoryTotalPaid = useMemo(() => {
    let amount = 0;
    if (apiData) {
      const paid = apiData.filter(
        (d) => d.status === TransactionProxyStatus.Success,
      );
      for (let index = 0; index < paid.length; index++) {
        amount += Number(paid[index].convertedAmount || paid[index].amount);
      }
    }
    return amount;
  }, [apiData]);

  const invoiceStatusGroup = useMemo(() => {
    const data: {
      name: string;
      value: number;
      color: string;
    }[] = [];
    if (apiData) {
      for (let index = 0; index < apiData.length; index++) {
        const item = apiData[index];
        const name =
          (messages["enums.transactionStatus"][item.status] as string) ||
          item.status;
        const findIndex = data.findIndex((d) => d.name == name);
        if (findIndex == -1) {
          data.push({
            name: name,
            value: 1,
            color: transactionStatusColor(item.status),
          });
        } else {
          data[findIndex].value++;
        }
      }
    }
    return data.sort((a, b) => b.value - a.value);
  }, [apiData]);

  return (
    <AppsContainer
      title={messages["sidebar.dashboard.paymentProvider"] as string}
      fullView
      cardStyle={{
        background: "transparent",
        boxShadow: "none",
      }}
      showDateRange={true}
      dateRangeValue={dateBetween}
      onDateRangeChange={handleDateBetween}
    >
      <AppAnimate animation="transition.slideUpIn" delay={200}>
        <AppRowContainer>
          <Col xs={24} md={12} lg={6} xxl={7}>
            <CustomPieChart
              title={messages["common.paymentHistory"] as string}
              graph="graph2"
              data={invoiceStatusGroup}
              refetch={paymentHistoryQuery.refetch}
              extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
              cardProps={{
                borderFull: true,
              }}
            />
          </Col>

          <Col xs={24} md={12} lg={6} xxl={6}>
            <Revenue
              title={messages["common.revenue"] as string}
              data={{
                currency: currency,
                paid: paymentHistoryTotalPaid,
                total: paymentHistoryTotalAmount,
              }}
              extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
              cardProps={{
                borderFull: true,
              }}
            />
          </Col>

          <Col xs={24} md={12} lg={6} xxl={5}>
            <AwaitingApprovalPayments />
          </Col>

          <Col xs={24} md={12} lg={6} xxl={6}>
            <AppRowContainer>
              <Col xs={24}>
                <MiniInfoCard
                  data={{
                    icon: <FaFilter />,
                    color: blue[5],
                    desc: <SelectedPaymentProviderSelectBox />,
                  }}
                  cardProps={{
                    borderFull: true,
                  }}
                />
              </Col>
              <Col xs={24}>
                <PaymentProvidersMiniCard
                  cardProps={{
                    borderFull: true,
                  }}
                />
              </Col>
              <Col xs={24}>
                <PaymentHistoriesMiniCard
                  useInWhichContext={AppContext.PAYMENT_PROVIDER}
                  cardProps={{
                    borderFull: true,
                  }}
                />
              </Col>
            </AppRowContainer>
          </Col>
        </AppRowContainer>

        <AppRowContainer>
          <Col xs={24} md={24} lg={24} style={{ marginBottom: 0 }}>
            <SharedPaymentHistory
              withoutContainer
              height={410}
              context={AppContext.PAYMENT_PROVIDER}
              tableProps={{
                columnsConfig: {
                  noFilter: true,
                  noSort: true,
                  noPagination: true,
                  hiddenColumns: ["updatedAt", "updatedBy", "id", "createdBy"],
                },
                header: {
                  title: (
                    <StyledFlexDiv style={{ gap: 10 }}>
                      <Typography.Text strong>
                        {messages["common.paymentHistory"] as string}
                      </Typography.Text>

                      <Link
                        to={ROUTES.ADMIN.PAYMENT_PROVIDER.PAYMENT_HISTORY.LIST}
                      >
                        {messages["common.viewAll"] as string}
                      </Link>
                    </StyledFlexDiv>
                  ),
                },
              }}
            />
          </Col>
        </AppRowContainer>
      </AppAnimate>
    </AppsContainer>
  );
}
